import type { PhotoReviewPayload, ReviewDraft, ReviewRow, ScreenSection } from '../types/photo'

export const lateralFields = new Set(['launch_direction', 'offline', 'spin_axis', 'club_path', 'face_angle', 'face_to_path'])

// Client feedback only: the server re-parses every value and checks canonical ranges.
export function signedValue(raw: string, lateral: boolean): string | null {
  let text = raw.trim()
  if (!text) return ''
  let direction = ''
  if (lateral) {
    const before = /^([LR])\s*(.+)$/i.exec(text)
    const after = /^(.+?)\s*([LR])$/i.exec(text)
    if (before || after) {
      direction = (before?.[1] ?? after![2]).toUpperCase()
      text = (before?.[2] ?? after![1]).trim()
      if (/^[+-]/.test(text)) return null
    }
  }
  if (!/^[+-]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) return null
  const normalized = `${direction === 'L' ? '-' : ''}${text.replace(/,/g, '')}`
  return Number.isFinite(Number(normalized)) ? normalized : null
}

export function initialReview(sections: ScreenSection[]): ReviewDraft[] {
  return [...sections].sort((a, b) => a.image - b.image).map(section => ({
    image: section.image,
    columns: Object.fromEntries(Object.entries(section.fields).map(([field, info]) => [field, { unit: info.unit ?? '', confirmed: false, dropped: false }])),
    rows: (section.kind === 'summary' ? (section.summary ? [section.summary] : []) : section.rows).map((row, index) => ({
      index, club: row.club ?? '', clubResolved: !row.flags.club, removed: false, keepOverlap: false,
      values: Object.fromEntries(Object.keys(section.fields).filter(field => field !== 'club').map(field => {
        const value = row.values[field]
        const text = value == null ? '' : section.fields[field].direction_style === 'letters' && lateralFields.has(field) && value !== 0
          ? `${Math.abs(value)} ${value < 0 ? 'L' : 'R'}` : String(value)
        return [field, { text, resolved: !row.flags[field] }]
      })),
    })),
    shotsReported: { text: section.summary?.shots_reported == null ? '' : String(section.summary.shots_reported), resolved: !section.summary?.flags.shots_reported },
  }))
}

export function reviewIssues(drafts: ReviewDraft[], sections: ScreenSection[], units: Record<string, string[]>, summary: boolean): string[] {
  const issues: string[] = []
  let rowCount = 0
  for (const draft of drafts) {
    const source = sections.find(section => section.image === draft.image)!
    const prefix = `Photo ${draft.image + 1}`
    for (const [field, column] of Object.entries(draft.columns)) {
      if (column.dropped) continue
      if (!column.confirmed) issues.push(`${prefix}: confirm ${source.fields[field].label}.`)
      if (field !== 'club' && !units[field]?.includes(column.unit)) issues.push(`${prefix}: choose the ${source.fields[field].label} unit.`)
    }
    for (const row of draft.rows.filter(row => !row.removed)) {
      rowCount++
      const label = `${prefix}, row ${row.index + 1}`
      if (!row.club.trim() || row.club.length > 60 || !row.clubResolved) issues.push(`${label}: check the club.`)
      const original = summary ? source.summary : source.rows[row.index]
      if (original?.row_flags.length && !row.keepOverlap) issues.push(`${label}: keep or remove this flagged row.`)
      let hasValue = false
      for (const [field, cell] of Object.entries(row.values)) {
        if (draft.columns[field].dropped) continue
        const parsed = signedValue(cell.text, lateralFields.has(field))
        if (!cell.resolved || parsed === null || cell.text.length > 30) issues.push(`${label}: check or clear ${source.fields[field].label}.`)
        if (parsed !== null && parsed !== '') hasValue = true
      }
      if (!hasValue) issues.push(`${label}: keep at least one measurement or remove the row.`)
    }
    if (summary && (!draft.shotsReported.resolved || (draft.shotsReported.text.trim() && (!/^\d+$/.test(draft.shotsReported.text) || Number(draft.shotsReported.text) < 1 || Number(draft.shotsReported.text) > 500)))) {
      issues.push(`${prefix}: check or clear the number of shots (1–500).`)
    }
  }
  if (!rowCount || rowCount > 300) issues.push('Keep between 1 and 300 rows.')
  return issues
}

export function reviewPayload(drafts: ReviewDraft[], summary: boolean): PhotoReviewPayload {
  const sections = drafts.map(draft => {
    const fields = Object.keys(draft.columns).filter(field => field !== 'club' && !draft.columns[field].dropped)
    const values = (row: ReviewRow) => Object.fromEntries(fields.map(field => [field, signedValue(row.values[field].text, lateralFields.has(field)) ?? '']))
    return { image: draft.image, units: Object.fromEntries(fields.map(field => [field, draft.columns[field].unit])),
      rows: draft.rows.filter(row => !row.removed).map(row => ({ index: row.index, club: row.club.trim(), values: values(row), keep_overlap: row.keepOverlap })) }
  })
  if (summary) return { summary: { club: sections[0].rows[0].club, shots_reported: drafts[0].shotsReported.text.trim(), values: sections[0].rows[0].values }, units: sections[0].units }
  return { sections }
}
